// Warning! Do not use this component directly in the app,
// it's used in the Tabs component. Use TabsItem component instead

import "./TabsItemInternal.scss"

import React, { FunctionComponent, useState, useRef, ReactNode } from "react"
import cx from "classnames"
import useClickOutside from "hooks/useClickOutside"
import TabsItem from "../TabsItem/TabsItem"

import ErrorIcon from "assets/img/icon-tab-error.svg?react"

interface ITabsItemInternalSubItem {
    index: number
    label: React.ReactNode
    isActive: boolean
    testId?: string
    error?: boolean
}

export interface ITabsItemInternalProps {
    id?: string
    label: string
    badge?: ReactNode
    index: number
    activeId: string
    activeIndex: number
    activeSubIndex: number
    subItemsAlign?: "left" | "right"
    subItemsWidth?: string
    onClick(index: number, subIndex?: number, id?: string): void
    testId?: string
    disabled?: boolean
    error?: boolean
}

const TabsItemInternal: FunctionComponent<React.PropsWithChildren<ITabsItemInternalProps>> = ({
    id,
    label,
    badge,
    index,
    activeId,
    activeIndex,
    activeSubIndex,
    subItemsAlign = "left",
    subItemsWidth,
    disabled,
    onClick,
    children,
    testId,
    error,
}) => {
    const [showSubItems, setShowSubItems] = useState(false)
    const tabItemRef = useRef(null)

    useClickOutside(() => {
        setShowSubItems(false)
    }, tabItemRef.current)

    if (children instanceof Array) {
        React.Children.map(children, c => {
            const child = c
            if (child && (!React.isValidElement(child) || child.type !== TabsItem)) {
                throw new Error("If you pass multiple elements into TabsItemInternal, they should also be TabsItem components")
            }
            if (child && child.props.children instanceof Array) {
                throw new Error("You cannot put TabsItem into nested TabsItem")
            }
        })
    } else {
        if (React.isValidElement(children) && children.type === TabsItem) {
            children = [children]
        }
    }

    const subItems: ITabsItemInternalSubItem[] | undefined | null = children instanceof Array
        ? React.Children
            .map(children, (c, subIndex) => {
                if (!c) return null
                const child = c as React.ReactElement<any, React.JSXElementConstructor<ITabsItemInternalProps>>

                return {
                    index: subIndex,
                    label: child.props.label,
                    isActive: ((id && id === activeId) || index === activeIndex) && subIndex === activeSubIndex,
                    testId: child.props.testId,
                    error: child.props.error
                }
            })
            .filter(Boolean)
        : null

    function handleClick(e) {
        e.preventDefault()
        if (disabled) return
        if (subItems?.length) {
            setShowSubItems(!showSubItems)
            return
        }

        if (typeof onClick !== "function" || typeof index === "undefined") return
        onClick(index, undefined, id)
    }

    function handleSubItemClick(subIndex: number) {
        if (typeof onClick !== "function" || typeof index === "undefined") return
        setShowSubItems(false)
        onClick(index, subIndex, id)
    }

    return (
        <div ref={tabItemRef} className={cx([
            "tabs-item",
            {
                "tabs-item--active": (id && id === activeId) || activeIndex === index,
                "tabs-item--has-subitems": !!subItems?.length,
                "tabs-item--disabled": disabled,
                "tabs-item--subitems-expanded": showSubItems,
            }
        ])}>
            <button onClick={handleClick}
                className={cx("tabs-item__btn", error && "color-red-500")}
                data-testid={testId}>
                {label}
                {!!error && <ErrorIcon className='ml-2' />}
                {!!badge &&
                    <div className="tabs-item__badge">{badge}</div>
                }
            </button>
            {showSubItems && !!subItems && subItems.length > 0 &&
                <ul className={cx([
                    "tabs-item__subitems",
                    `tabs-item__subitems--align-${subItemsAlign}`
                ])}
                    style={{ width: subItemsWidth }}>
                    {subItems.map(subItem => (
                        <li key={subItem.index} className="tabs-item__subitems-item">
                            <button onClick={(e) => {
                                e.preventDefault()
                                handleSubItemClick(subItem.index)
                            }}
                                className={cx("tabs-item__subitems-btn", subItem.error && 'color-red-500')}
                                data-testid={subItem.testId}>
                                {subItem.label}
                                {subItem.error && <ErrorIcon className='ml-2' />}
                            </button>
                        </li>
                    ))}
                </ul>
            }
        </div>
    )
}

export default TabsItemInternal
